(() => {
  'use strict';

  const lovePsRoot = document.querySelector('[data-love-ps-root]:not([data-love-ps-initialized])');
  if (!lovePsRoot) return;
  lovePsRoot.dataset.lovePsInitialized = 'true';

  class LovePsStarfield {
    constructor(canvas) {
      this.canvas = canvas;
      this.context = canvas?.getContext('2d');
      this.stars = [];
      this.animationFrame = null;
      this.lastTime = 0;
      this.frameAccumulator = 0;
      this.frameInterval = 82;
      this.pixelRatio = 1;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.resizeObserver = this.canvas ? new ResizeObserver(() => this.resize()) : null;
      this.resizeObserver?.observe(this.canvas);
    }

    resize() {
      if (!this.canvas || !this.context || this.canvas.clientWidth === 0) return;
      const rect = this.canvas.getBoundingClientRect();
      this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.max(1, Math.floor(rect.width * this.pixelRatio));
      this.canvas.height = Math.max(1, Math.floor(rect.height * this.pixelRatio));
      this.context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      const count = Math.max(54, Math.floor((rect.width * rect.height) / 4700));
      this.stars = Array.from({ length: count }, () => this.createStar(true));
      if (this.reducedMotion.matches) this.draw(performance.now(), 0);
    }

    createStar(anywhere = false) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.sqrt(Math.random()) * 1.18;
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        z: anywhere ? .18 + Math.random() * .82 : .96 + Math.random() * .04,
        speed: .00018 + Math.random() * .00011,
        tint: Math.random() > .86
      };
    }

    start() {
      if (!this.canvas || !this.context || this.animationFrame) return;
      this.resize();
      if (this.reducedMotion.matches) return;
      this.lastTime = performance.now();
      this.animationFrame = requestAnimationFrame((time) => this.tick(time));
    }

    stop() {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    tick(time) {
      const elapsed = Math.min(40, time - this.lastTime);
      this.lastTime = time;
      this.frameAccumulator += elapsed;
      if (this.frameAccumulator >= this.frameInterval) {
        const stepElapsed = Math.min(120, this.frameAccumulator);
        this.frameAccumulator = Math.random() * 18;
        this.draw(time, stepElapsed);
      }
      this.animationFrame = requestAnimationFrame((nextTime) => this.tick(nextTime));
    }

    draw(time, stepElapsed) {
      if (!this.canvas || !this.context) return;
      const rect = this.canvas.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const projectionScale = Math.min(rect.width, rect.height) * .56;
      this.context.clearRect(0, 0, rect.width, rect.height);
      this.stars.forEach((star, index) => {
        const previousZ = star.z;
        star.z -= star.speed * stepElapsed;
        if (star.z <= .08) { this.stars[index] = this.createStar(); return; }
        const previousScale = projectionScale / previousZ;
        const scale = projectionScale / star.z;
        const previousX = centerX + star.x * previousScale;
        const previousY = centerY + star.y * previousScale;
        const screenX = centerX + star.x * scale;
        const screenY = centerY + star.y * scale;
        if (screenX < -24 || screenX > rect.width + 24 || screenY < -24 || screenY > rect.height + 24) {
          this.stars[index] = this.createStar();
          return;
        }
        const closeness = 1 - star.z;
        const size = Math.max(2, Math.min(7, Math.floor(2 + closeness * 7)));
        const alpha = Math.min(.68, .16 + closeness * .62);
        const x = Math.round(screenX / 2) * 2;
        const y = Math.round(screenY / 2) * 2;
        this.context.globalAlpha = alpha * .42;
        this.context.strokeStyle = star.tint ? 'rgb(255, 39, 173)' : 'rgb(244, 244, 242)';
        this.context.lineWidth = Math.max(1, Math.floor(size * .55));
        this.context.beginPath();
        this.context.moveTo(Math.round(previousX / 2) * 2, Math.round(previousY / 2) * 2);
        this.context.lineTo(x, y);
        this.context.stroke();
        this.context.globalAlpha = alpha;
        this.context.fillStyle = 'rgb(244, 244, 242)';
        this.context.fillRect(x, y, size, size);
        if (star.tint && size > 3) {
          this.context.globalAlpha = alpha * .44;
          this.context.fillStyle = 'rgb(255, 39, 173)';
          this.context.fillRect(x - size, y - size, Math.max(2, size - 2), Math.max(2, size - 2));
        }
      });
      this.context.globalAlpha = 1;
    }
  }

  const lovePsPatches = [
    ['dub-bass-2', 'Dub Bass 2.0', 'Marc Barnes'],
    ['duo-hive', 'Duo Hive', 'Marc Barnes'],
    ['first-reese', 'First Reese', 'Marc Barnes'],
    ['india-2', 'Ravi 2.0', 'Marc Barnes'],
    ['insideous-keys', 'Insideous Keys', 'Marc Barnes'],
    ['neuro-fracture', 'Neuro Fracture', 'Marc Barnes'],
    ['polar-dreams', 'Polar Dreams', 'Marc Barnes'],
    ['popcorn-2', 'Popcorn 2.0', 'Marc Barnes'],
    ['power-snare', 'Power Snare', 'Marc Barnes'],
    ['qanun', 'Qanun', 'Marc Barnes'],
    ['steel-hat', 'Steel Hat', 'Marc Barnes'],
    ['syfi-pad', 'Syfi Pad', 'Marc Barnes'],
    ['warm-pad', 'Warm Pad', 'Marc Barnes'],
    ['digital-silk', 'Digital Silk', 'Marc Barnes'],
    ['access-fm', 'Access FM', 'Marc Barnes'],
    ['alloy-hat', 'Alloy Hat', 'Marc Barnes'],
    ['fm-kik', 'FM Kik', 'Marc Barnes'],
    ['power-tom', 'Power Tom', 'Marc Barnes'],
    ['velvet-ep', 'Velvet EP', 'Marc Barnes'],
    ['aphex-pluck', 'Aphex Pluck', 'Marc Barnes'],
    ['electro-acoustic', 'Electro Acoustic', 'Marc Barnes'],
    ['army-bass', 'Army Bass', 'Marc Barnes'],
    ['house-organ', 'House Organ', 'Marc Barnes'],
    ['saw-funk', 'Saw Funk', 'Marc Barnes'],
    ['timber-bass', 'Timber Bass', 'Marc Barnes'],
    ['daft-what', 'Aprés', 'Thoracius Appotite'],
    ['froggy', 'Froskur', 'Steinunn Eldflaug'],
    ['hvalur', 'Hvalur', 'Thoracius Appotite'],
    ['kick', 'Kylie Kick', 'Thoracius Appotite'],
    ['pop', 'POP!', 'Thoracius Appotite'],
    ['two-question-marks', 'TwoQuestionMarks', 'Thoracius Appotite']
  ];

  const lovePsGenres = {
    'dub-bass-2': ['Staff', 'Bass'],
    'duo-hive': ['Pad'],
    'first-reese': ['Staff', 'Bass'],
    'india-2': ['Staff', 'Keys', 'Pluck'],
    'insideous-keys': ['Staff', 'Keys'],
    'neuro-fracture': ['Staff', 'Lead', 'Bass'],
    'polar-dreams': ['Pad'],
    'popcorn-2': ['Staff', 'Pluck'],
    'power-snare': ['Perc'],
    'qanun': ['Keys', 'Pluck'],
    'steel-hat': ['Perc'],
    'syfi-pad': ['Staff', 'Pad'],
    'warm-pad': ['Staff', 'Pad'],
    'digital-silk': ['Staff', 'Keys', 'Pad'],
    'access-fm': ['Staff', 'Lead', 'Pluck'],
    'alloy-hat': ['Perc'],
    'fm-kik': ['Staff', 'Perc'],
    'power-tom': ['Staff', 'Perc'],
    'velvet-ep': ['Keys'],
    'aphex-pluck': ['Staff', 'Pluck'],
    'electro-acoustic': ['Staff', 'Keys', 'Pluck'],
    'army-bass': ['Staff', 'Bass'],
    'house-organ': ['Staff', 'Keys', 'Bass'],
    'saw-funk': ['Lead'],
    'timber-bass': ['Bass'],
    'daft-what': ['Staff', 'Lead', 'Bass'],
    'froggy': ['FX'],
    'hvalur': ['Bass'],
    'kick': ['Perc'],
    'pop': ['Pluck'],
    'two-question-marks': ['Staff', 'Lead', 'Bass']
  };

  const lovePsCategoryOrder = {
    Staff: [
      'warm-pad',
      'first-reese',
      'insideous-keys',
      'neuro-fracture',
      'india-2',
      'daft-what',
      'dub-bass-2',
      'popcorn-2',
      'syfi-pad',
      'digital-silk',
      'aphex-pluck',
      'access-fm',
      'fm-kik',
      'power-tom',
      'electro-acoustic',
      'army-bass',
      'house-organ',
      'two-question-marks'
    ],
    Perc: ['fm-kik', 'kick', 'alloy-hat', 'steel-hat', 'power-snare', 'power-tom']
  };

  const lovePsPatchOrder = new Map(lovePsPatches.map(([slug], index) => [slug, index]));

  const lovePsDetails = {
    'insideous-keys': {
      links: [{ label: 'Watch Insideous Keys in context', url: 'https://youtube.com/shorts/iwR9atQpfNg?feature=share' }]
    },
    'popcorn-2': {
      links: [{ label: 'Watch Popcorn 2.0 in context', url: 'https://youtube.com/shorts/ByF9pFpSJWs' }]
    },
    'house-organ': {
      links: [{ label: 'Watch House Organ in context', url: 'https://youtube.com/shorts/ByF9pFpSJWs' }]
    }
  };

  const lovePsList = lovePsRoot.querySelector('[data-love-ps-list]');
  const lovePsScaler = lovePsRoot.querySelector('[data-love-ps-scaler]');
  const lovePsScrollUp = lovePsRoot.querySelector('[data-love-ps-scroll-up]');
  const lovePsScrollDown = lovePsRoot.querySelector('[data-love-ps-scroll-down]');
  const lovePsScrollTrack = lovePsRoot.querySelector('[data-love-ps-scroll-track]');
  const lovePsScrollThumb = lovePsRoot.querySelector('[data-love-ps-scroll-thumb]');
  const lovePsGenreFilter = lovePsRoot.querySelector('[data-love-ps-genre-filter]');
  const lovePsGenreTrigger = lovePsRoot.querySelector('[data-love-ps-genre-trigger]');
  const lovePsGenreValue = lovePsRoot.querySelector('[data-love-ps-genre-value]');
  const lovePsGenreMenu = lovePsRoot.querySelector('[data-love-ps-genre-menu]');
  const lovePsGenreOptions = [...lovePsRoot.querySelectorAll('[data-love-ps-genre-option]')];
  const lovePsWindow = lovePsRoot.querySelector('[data-love-ps-window]');
  const lovePsTitlebar = lovePsWindow?.querySelector('.love-ps-titlebar');
  const lovePsWindowClose = lovePsRoot.querySelector('[data-love-ps-window-close]');
  const lovePsWindowMaximize = lovePsRoot.querySelector('[data-love-ps-window-maximize]');
  const lovePsIsDedicatedPage = lovePsRoot.dataset.lovePsDedicatedPage === 'true';
  const lovePsPageUrl = lovePsRoot.dataset.lovePsPageUrl || '/pages/patchstation';
  const lovePsResizeHandle = lovePsRoot.querySelector('[data-love-ps-resize-handle]');
  const lovePsDesktopIcon = lovePsRoot.querySelector('[data-love-ps-desktop-icon]');
  const lovePsDesktopIconImage = lovePsRoot.querySelector('[data-love-ps-desktop-icon-image]');
  const lovePsWindowStrobe = lovePsRoot.querySelector('[data-love-ps-window-strobe]');
  const lovePsWindowDragOutline = lovePsRoot.querySelector('[data-love-ps-window-drag-outline]');
  const lovePsMobileMedia = window.matchMedia('(max-width: 899px)');
  const lovePsConnectIcon = lovePsRoot.querySelector('[data-love-ps-connect-icon]');
  const lovePsConnectIconImage = lovePsRoot.querySelector('[data-love-ps-connect-icon-image]');
  const lovePsInfoWindow = lovePsRoot.querySelector('[data-love-ps-info-window]');
  const lovePsInfoOk = lovePsRoot.querySelector('[data-love-ps-info-ok]');
  const lovePsInfoStrobe = lovePsRoot.querySelector('[data-love-ps-info-strobe]');
  const lovePsInfoVisual = lovePsRoot.querySelector('.love-ps-info-visual');
  const lovePsWaveController = lovePsRoot.querySelector('[data-love-ps-wave-controller]');
  const lovePsModWavePath = lovePsRoot.querySelector('[data-love-ps-mod-wave-path]');
  const lovePsNewsletterButton = lovePsRoot.querySelector('[data-love-ps-newsletter]');
  const lovePsStarfield = new LovePsStarfield(lovePsRoot.querySelector('[data-love-ps-starfield]'));
  const lovePsDesktopPatches = lovePsRoot.querySelector('[data-love-ps-desktop-patches]');
  const lovePsTrash = lovePsRoot.querySelector('[data-love-ps-trash]');
  const lovePsTrashImage = lovePsRoot.querySelector('[data-love-ps-trash-image]');
  let lovePsDesktopPatchCounter = 0;
  let lovePsWaveDuration = 900;
  let lovePsWaveAmplitude = 8;
  let lovePsWaveDrag = null;
  if (!lovePsList || !lovePsScaler) return;

  function lovePsClampWave(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function lovePsSetWaveModulation(duration, amplitude) {
    lovePsWaveDuration = Math.round(lovePsClampWave(duration, 240, 1800));
    lovePsWaveAmplitude = Math.round(lovePsClampWave(amplitude, 0, 30));
    lovePsInfoWindow.style.setProperty('--love-ps-wave-duration', `${lovePsWaveDuration}ms`);
    lovePsInfoWindow.style.setProperty('--love-ps-wave-amplitude', `${lovePsWaveAmplitude}px`);
    lovePsInfoWindow.style.setProperty('--love-ps-wave-amplitude-negative', `${-lovePsWaveAmplitude}px`);
    lovePsInfoWindow.style.setProperty('--love-ps-wave-half-amplitude', `${lovePsWaveAmplitude / 2}px`);
    lovePsInfoWindow.style.setProperty('--love-ps-wave-half-amplitude-negative', `${-lovePsWaveAmplitude / 2}px`);
    lovePsInfoVisual.querySelectorAll('.love-ps-info-patch-group').forEach((group) => {
      const patches = [...group.querySelectorAll('img')];
      patches.forEach((patch, index) => {
        patch.style.animationDelay = `${-(index * lovePsWaveDuration / 5)}ms`;
      });
    });
    const waveCycles = 1.5 + ((1800 - lovePsWaveDuration) / 1560) * 6.5;
    const wavePoints = [];
    for (let x = 0; x <= 760; x += 6) {
      const y = 50 - Math.sin((x / 760) * Math.PI * 2 * waveCycles) * lovePsWaveAmplitude;
      wavePoints.push(`${x === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    lovePsModWavePath.setAttribute('d', wavePoints.join(' '));
    lovePsWaveController.setAttribute(
      'aria-valuetext',
      `Frequency ${Math.round(90000 / lovePsWaveDuration) / 100}, amplitude ${lovePsWaveAmplitude}`
    );
  }

  function lovePsStartWaveModulation(event) {
    if (event.button !== 0) return;
    event.preventDefault();
    lovePsWaveController.classList.remove('love-ps-just-released');
    lovePsInfoVisual.classList.add('love-ps-is-wave-active');
    lovePsWaveController.setAttribute('aria-pressed', 'true');
    lovePsWaveDrag = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      duration: lovePsWaveDuration,
      amplitude: lovePsWaveAmplitude
    };
    lovePsWaveController.setPointerCapture(event.pointerId);
    lovePsWaveController.classList.add('love-ps-is-modulating');
  }

  function lovePsMoveWaveModulation(event) {
    if (!lovePsWaveDrag || event.pointerId !== lovePsWaveDrag.pointerId) return;
    const deltaX = event.clientX - lovePsWaveDrag.x;
    const deltaY = event.clientY - lovePsWaveDrag.y;
    lovePsSetWaveModulation(
      lovePsWaveDrag.duration - deltaX * 4,
      lovePsWaveDrag.amplitude - deltaY * .18
    );
  }

  function lovePsEndWaveModulation(event) {
    if (!lovePsWaveDrag || event.pointerId !== lovePsWaveDrag.pointerId) return;
    lovePsWaveDrag = null;
    lovePsWaveController.classList.remove('love-ps-is-modulating');
    lovePsWaveController.classList.add('love-ps-just-released');
    lovePsInfoVisual.classList.remove('love-ps-is-wave-active');
    lovePsWaveController.setAttribute('aria-pressed', 'false');
  }

  lovePsSetWaveModulation(lovePsWaveDuration, lovePsWaveAmplitude);

  const lovePsAudio = new Audio();
  lovePsAudio.preload = 'none';
  let lovePsActiveRow = null;
  let lovePsExpandedRow = null;
  let lovePsSelectedRow = null;
  let lovePsFrame = null;
  let lovePsThumbDrag = null;
  let lovePsWindowPositioned = false;
  let lovePsWindowDrag = null;
  let lovePsWindowResizeTimer = null;
  let lovePsWindowMaximized = false;
  let lovePsWindowRestoreGeometry = null;

  const lovePsAssetBase = new URL(lovePsRoot.dataset.lovePsAssetProbe, window.location.href);
  const lovePsAudioBase = new URL(lovePsRoot.dataset.lovePsAudioProbe, window.location.href);
  const lovePsCacheVersion = lovePsRoot.dataset.lovePsCacheVersion || '1';
  const lovePsVersionedUrl = (name, base) => {
    const url = new URL(name, base);
    url.searchParams.set('love-ps-v', lovePsCacheVersion);
    return url.href;
  };
  const lovePsAsset = (name) => lovePsVersionedUrl(name, lovePsAssetBase);
  const lovePsAudioAsset = (name) => lovePsVersionedUrl(name, lovePsAudioBase);

  function lovePsCreateRow([imageSlug, title, artist]) {
    const imageVersion = imageSlug === 'warm-pad'
      ? '20260820b'
      : imageSlug === 'first-reese'
        ? '20260820'
        : '20260819';
    const imageAssetName = `love-ps-${imageVersion}-patch-${imageSlug}`;
    const row = document.createElement('article');
    row.className = 'love-ps-row';
    row.dataset.lovePsAudio = lovePsAudioAsset(`love-ps-audio-${imageSlug}.mp3`);
    row.dataset.lovePsSlug = imageSlug;
    row.dataset.lovePsGenres = lovePsGenres[imageSlug].join(' ');
    row.dataset.lovePsImage = lovePsAsset(`${imageAssetName}.png`);
    row.dataset.lovePsSelectedImage = lovePsAsset(`${imageAssetName}-selected.png`);
    row.dataset.lovePsTitle = title;
    const details = lovePsDetails[imageSlug];
    const detailId = `love-ps-detail-${imageSlug}`;
    const detailMarkup = details ? `
      <button class="love-ps-expand-button" type="button" aria-expanded="false" aria-controls="${detailId}" aria-label="Show details for ${title}">
        <span aria-hidden="true"></span>
      </button>
      <div class="love-ps-patch-detail" id="${detailId}" hidden>
        ${details.description ? `<p>${details.description}</p>` : ''}
        <div class="love-ps-patch-links">
          ${details.links.map((link) => `<a href="${link.url}" target="_blank" rel="noopener noreferrer"><span class="love-ps-youtube-mark" aria-hidden="true">▶</span><span>${link.label}</span></a>`).join('')}
        </div>
      </div>` : '<span class="love-ps-expand-spacer" aria-hidden="true"></span>';
    row.classList.toggle('love-ps-has-details', Boolean(details));
    row.innerHTML = `
      <span class="love-ps-art-wrap" aria-hidden="true">
        <img class="love-ps-art love-ps-art-default" src="${lovePsAsset(`${imageAssetName}.png`)}" alt="" width="86" height="86" loading="eager" decoding="async">
        <img class="love-ps-art love-ps-art-selected" src="${lovePsAsset(`${imageAssetName}-selected.png`)}" alt="" width="86" height="86" loading="eager" decoding="async">
      </span>
      <div class="love-ps-meta">
        <span class="love-ps-patch-title">${title}</span>
        <span class="love-ps-patch-creator"><span class="love-ps-creator-desktop">${artist}</span><span class="love-ps-creator-mobile">${artist}</span></span>
      </div>
      <div class="love-ps-playback">
        <button class="love-ps-play-button" type="button" aria-label="Play ${title}">
          <img src="${lovePsAsset('love-ps-play.png')}" alt="" width="28" height="28">
        </button>
        <div class="love-ps-progress" role="progressbar" aria-label="${title} preview progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <span class="love-ps-progress-fill"></span>
        </div>
      </div>
      ${detailMarkup}`;
    row.querySelector('.love-ps-play-button').addEventListener('click', () => lovePsToggle(row));
    row.querySelector('.love-ps-expand-button')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      lovePsToggleDetails(row);
    });
    row.addEventListener('click', (event) => {
      if (!lovePsMobileMedia.matches || event.target.closest('.love-ps-playback, .love-ps-expand-button, .love-ps-patch-detail')) return;
      if (lovePsSelectedRow && lovePsSelectedRow !== row) {
        lovePsSelectedRow.classList.remove('love-ps-is-selected');
        if (lovePsSelectedRow.classList.contains('love-ps-is-expanded')) lovePsSetDetailsOpen(lovePsSelectedRow, false);
        lovePsSelectedRow = null;
      }
      const hasDetails = row.classList.contains('love-ps-has-details');
      const selected = hasDetails
        ? !row.classList.contains('love-ps-is-expanded')
        : !row.classList.contains('love-ps-is-selected');
      row.classList.toggle('love-ps-is-selected', selected);
      lovePsSelectedRow = selected ? row : null;
      if (hasDetails) lovePsToggleDetails(row);
    });
    row.addEventListener('pointerdown', (event) => lovePsStartPatchCopyDrag(event, row));
    return row;
  }

  function lovePsSetDetailsOpen(row, open) {
    if (!row) return;
    const button = row.querySelector('.love-ps-expand-button');
    const detail = row.querySelector('.love-ps-patch-detail');
    if (!button || !detail) return;
    row.classList.toggle('love-ps-is-expanded', open);
    button.setAttribute('aria-expanded', String(open));
    button.setAttribute('aria-label', `${open ? 'Hide' : 'Show'} details for ${row.dataset.lovePsTitle}`);
    detail.hidden = !open;
    if (open) lovePsExpandedRow = row;
    else if (lovePsExpandedRow === row) lovePsExpandedRow = null;
  }

  function lovePsToggleDetails(row) {
    const open = !row.classList.contains('love-ps-is-expanded');
    if (lovePsMobileMedia.matches) {
      if (lovePsSelectedRow && lovePsSelectedRow !== row) {
        lovePsSelectedRow.classList.remove('love-ps-is-selected');
        if (lovePsSelectedRow.classList.contains('love-ps-is-expanded')) {
          lovePsSetDetailsOpen(lovePsSelectedRow, false);
        }
      }
      row.classList.toggle('love-ps-is-selected', open);
      lovePsSelectedRow = open ? row : null;
    }
    if (lovePsExpandedRow && lovePsExpandedRow !== row) lovePsSetDetailsOpen(lovePsExpandedRow, false);
    lovePsSetDetailsOpen(row, open);
    requestAnimationFrame(() => {
      lovePsUpdateScrollbar();
      if (open) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  function lovePsPointInElement(x, y, element) {
    const rect = element.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  function lovePsSetTrashActive(active) {
    lovePsTrash.setAttribute('aria-pressed', String(active));
    lovePsTrashImage.src = lovePsAsset(active ? 'love-ps-trash-active.png' : 'love-ps-trash.png');
  }

  function lovePsSetSynthDropActive(active) {
    lovePsConnectIcon.classList.toggle('love-ps-is-drop-target', active);
    if (lovePsInfoWindow.hidden) {
      lovePsConnectIconImage.src = lovePsAsset(active
        ? 'love-ps-connect-first-love-selected.png'
        : 'love-ps-connect-first-love.png');
    }
  }

  function lovePsMakeDragGhost(imageSrc, title) {
    const ghost = document.createElement('div');
    ghost.className = 'love-ps-patch-drag-ghost';
    ghost.innerHTML = `<img src="${imageSrc}" alt=""><span>${title}</span>`;
    document.body.append(ghost);
    return ghost;
  }

  function lovePsPlaceDesktopPatch(patch, clientX, clientY) {
    const pageRect = lovePsRoot.getBoundingClientRect();
    const width = patch.offsetWidth || 150;
    const height = patch.offsetHeight || 112;
    patch.style.left = `${Math.max(0, Math.min(pageRect.width - width, clientX - pageRect.left - width / 2))}px`;
    patch.style.top = `${Math.max(0, Math.min(pageRect.height - height, clientY - pageRect.top - height / 2))}px`;
  }

  function lovePsCreateDesktopPatch(imageSrc, title, clientX, clientY, shouldFocus = true) {
    const patch = document.createElement('button');
    patch.className = 'love-ps-desktop-patch';
    patch.type = 'button';
    patch.dataset.lovePsDesktopPatchId = String(++lovePsDesktopPatchCounter);
    patch.setAttribute('aria-label', `${title}, desktop patch`);
    patch.innerHTML = `<img src="${imageSrc}" alt=""><span>${title}</span>`;
    lovePsDesktopPatches.append(patch);
    lovePsPlaceDesktopPatch(patch, clientX, clientY);
    patch.addEventListener('pointerdown', (event) => lovePsStartDesktopPatchDrag(event, patch));
    if (shouldFocus) patch.focus();
  }

  function lovePsSeedDesktopPatch() {
    if (lovePsMobileMedia.matches || lovePsDesktopPatches.children.length) return;
    const pageRect = lovePsRoot.getBoundingClientRect();
    const clientX = pageRect.left + Math.min(230, pageRect.width * .16);
    const clientY = pageRect.top + Math.min(pageRect.height - 170, pageRect.height * .64);
    lovePsCreateDesktopPatch(
      lovePsAsset('love-ps-20260819-patch-popcorn-2.png'),
      'Popcorn 2.0',
      clientX,
      clientY,
      false
    );
  }

  function lovePsClampDesktopPatches() {
    const page = lovePsRoot;
    const maxPageWidth = page.clientWidth;
    const maxPageHeight = page.clientHeight;
    lovePsDesktopPatches.querySelectorAll('.love-ps-desktop-patch').forEach((patch) => {
      const left = Number.parseFloat(patch.style.left) || 0;
      const top = Number.parseFloat(patch.style.top) || 0;
      const maxLeft = Math.max(0, maxPageWidth - patch.offsetWidth);
      const maxTop = Math.max(0, maxPageHeight - patch.offsetHeight);
      patch.style.left = `${Math.max(0, Math.min(left, maxLeft))}px`;
      patch.style.top = `${Math.max(0, Math.min(top, maxTop))}px`;
    });
  }

  function lovePsStartPatchCopyDrag(event, row) {
    if (lovePsMobileMedia.matches || event.button !== 0 || event.target.closest('.love-ps-playback, .love-ps-expand-button, .love-ps-patch-detail')) return;
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    let ghost = null;
    const imageSrc = row.dataset.lovePsImage;
    const ghostImageSrc = row.dataset.lovePsSelectedImage;
    const title = row.dataset.lovePsTitle;
    const move = (moveEvent) => {
      if (!ghost && Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < 7) return;
      if (!ghost) {
        ghost = lovePsMakeDragGhost(ghostImageSrc, title);
        row.classList.add('love-ps-is-copy-dragging');
      }
      ghost.style.left = `${moveEvent.clientX}px`;
      ghost.style.top = `${moveEvent.clientY}px`;
      lovePsSetSynthDropActive(lovePsPointInElement(moveEvent.clientX, moveEvent.clientY, lovePsConnectIcon));
    };
    const end = (endEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      ghost?.remove();
      row.classList.remove('love-ps-is-copy-dragging');
      lovePsSetSynthDropActive(false);
      if (ghost && lovePsPointInElement(endEvent.clientX, endEvent.clientY, lovePsConnectIcon)) {
        lovePsOpenInfoWindow();
        return;
      }
      if (!ghost || lovePsPointInElement(endEvent.clientX, endEvent.clientY, lovePsWindow)) return;
      if (lovePsPointInElement(endEvent.clientX, endEvent.clientY, lovePsTrash)) return;
      lovePsCreateDesktopPatch(imageSrc, title, endEvent.clientX, endEvent.clientY);
    };
    const cancel = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      ghost?.remove();
      row.classList.remove('love-ps-is-copy-dragging');
      lovePsSetSynthDropActive(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', cancel);
  }

  function lovePsStartDesktopPatchDrag(event, patch) {
    if (lovePsMobileMedia.matches || event.button !== 0) return;
    event.preventDefault();
    const title = patch.querySelector('span').textContent;
    const imageSrc = patch.querySelector('img').src;
    const ghost = lovePsMakeDragGhost(imageSrc, title);
    patch.classList.add('love-ps-is-dragging');
    patch.style.visibility = 'hidden';
    const move = (moveEvent) => {
      ghost.style.left = `${moveEvent.clientX}px`;
      ghost.style.top = `${moveEvent.clientY}px`;
      lovePsSetTrashActive(lovePsPointInElement(moveEvent.clientX, moveEvent.clientY, lovePsTrash));
      lovePsSetSynthDropActive(lovePsPointInElement(moveEvent.clientX, moveEvent.clientY, lovePsConnectIcon));
    };
    const finish = (endEvent, cancelled = false) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      ghost.remove();
      lovePsSetTrashActive(false);
      lovePsSetSynthDropActive(false);
      if (!cancelled && lovePsPointInElement(endEvent.clientX, endEvent.clientY, lovePsTrash)) {
        patch.remove();
        return;
      }
      patch.style.visibility = '';
      patch.classList.remove('love-ps-is-dragging');
      if (!cancelled && lovePsPointInElement(endEvent.clientX, endEvent.clientY, lovePsConnectIcon)) {
        lovePsOpenInfoWindow();
        return;
      }
      if (!cancelled) lovePsPlaceDesktopPatch(patch, endEvent.clientX, endEvent.clientY);
      patch.focus();
    };
    const end = (endEvent) => finish(endEvent);
    const cancel = (cancelEvent) => finish(cancelEvent, true);
    move(event);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', cancel);
  }

  function lovePsResetRow(row, resetProgress = true) {
    if (!row) return;
    row.classList.remove('love-ps-is-playing');
    const button = row.querySelector('.love-ps-play-button');
    const title = row.querySelector('.love-ps-patch-title').textContent;
    button.setAttribute('aria-label', `Play ${title}`);
    button.querySelector('img').src = lovePsAsset('love-ps-play.png');
    if (resetProgress) lovePsSetProgress(row, 0);
  }

  function lovePsSetProgress(row, value) {
    const progress = row.querySelector('.love-ps-progress');
    progress.style.setProperty('--love-ps-progress', `${value}%`);
    progress.setAttribute('aria-valuenow', String(Math.round(value)));
  }

  function lovePsUpdateProgress() {
    if (!lovePsActiveRow) return;
    const ratio = lovePsAudio.duration ? lovePsAudio.currentTime / lovePsAudio.duration : 0;
    lovePsSetProgress(lovePsActiveRow, Math.min(100, ratio * 100));
    lovePsFrame = requestAnimationFrame(lovePsUpdateProgress);
  }

  function lovePsToggle(row) {
    if (lovePsMobileMedia.matches && lovePsSelectedRow !== row) {
      if (lovePsSelectedRow) {
        lovePsSelectedRow.classList.remove('love-ps-is-selected');
        if (lovePsSelectedRow.classList.contains('love-ps-is-expanded')) {
          lovePsSetDetailsOpen(lovePsSelectedRow, false);
        }
      }
      row.classList.add('love-ps-is-selected');
      lovePsSelectedRow = row;
    }

    if (lovePsActiveRow === row && !lovePsAudio.paused) {
      lovePsAudio.pause();
      lovePsResetRow(row);
      lovePsActiveRow = null;
      cancelAnimationFrame(lovePsFrame);
      return;
    }

    lovePsAudio.pause();
    lovePsResetRow(lovePsActiveRow);
    cancelAnimationFrame(lovePsFrame);
    lovePsActiveRow = row;
    lovePsAudio.src = row.dataset.lovePsAudio;
    lovePsAudio.currentTime = 0;
    row.classList.add('love-ps-is-playing');
    const button = row.querySelector('.love-ps-play-button');
    const title = row.querySelector('.love-ps-patch-title').textContent;
    button.setAttribute('aria-label', `Pause ${title}`);
    button.querySelector('img').src = lovePsAsset('love-ps-pause-active.png');
    lovePsAudio.play().then(lovePsUpdateProgress).catch(() => {
      lovePsResetRow(row);
      lovePsActiveRow = null;
    });
  }

  function lovePsResize() {
    const rootDocumentTop = lovePsRoot.getBoundingClientRect().top + window.scrollY;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const footerPeek = lovePsMobileMedia.matches ? 54 : 0;
    const minimumHeight = lovePsMobileMedia.matches ? 440 : 520;
    lovePsRoot.style.setProperty('--love-ps-available-height', `${Math.max(minimumHeight, viewportHeight - rootDocumentTop - footerPeek)}px`);
    requestAnimationFrame(() => {
      lovePsKeepWindowInBounds();
      lovePsUpdateScrollbar();
      lovePsClampDesktopPatches();
    });
  }

  function lovePsSetWindowPosition(left, top) {
    lovePsScaler.style.left = `${Math.round(left)}px`;
    lovePsScaler.style.top = `${Math.round(top)}px`;
    lovePsScaler.style.transform = 'none';
    lovePsWindowPositioned = true;
  }

  function lovePsKeepWindowInBounds() {
    if (lovePsMobileMedia.matches) {
      lovePsWindowDragOutline.hidden = true;
      lovePsScaler.classList.remove('love-ps-is-moving', 'love-ps-is-maximized', 'love-ps-is-resized');
      lovePsScaler.style.removeProperty('left');
      lovePsScaler.style.removeProperty('top');
      lovePsScaler.style.removeProperty('width');
      lovePsScaler.style.removeProperty('height');
      lovePsScaler.style.removeProperty('transform');
      lovePsWindowPositioned = false;
      lovePsWindowMaximized = false;
      lovePsWindowRestoreGeometry = null;
      lovePsWindowMaximize.setAttribute('aria-pressed', 'false');
      lovePsWindowMaximize.setAttribute('aria-label', 'Maximize Patch Station window');
      return;
    }

    const desktopRect = lovePsRoot.getBoundingClientRect();
    if (lovePsWindowMaximized) {
      lovePsUpdateScrollbar();
      return;
    }
    let windowRect = lovePsScaler.getBoundingClientRect();
    if (windowRect.width > desktopRect.width) lovePsScaler.style.width = `${Math.round(desktopRect.width)}px`;
    if (windowRect.height > desktopRect.height) lovePsScaler.style.height = `${Math.round(desktopRect.height)}px`;
    windowRect = lovePsScaler.getBoundingClientRect();
    const currentLeft = lovePsWindowPositioned
      ? Number.parseFloat(lovePsScaler.style.left) || 0
      : windowRect.left - desktopRect.left;
    const currentTop = lovePsWindowPositioned
      ? Number.parseFloat(lovePsScaler.style.top) || 0
      : windowRect.top - desktopRect.top;
    const maxLeft = Math.max(0, desktopRect.width - windowRect.width);
    const maxTop = Math.max(0, desktopRect.height - windowRect.height);
    lovePsSetWindowPosition(
      Math.min(maxLeft, Math.max(0, currentLeft)),
      Math.min(maxTop, Math.max(0, currentTop))
    );

    window.clearTimeout(lovePsWindowResizeTimer);
    lovePsWindowResizeTimer = window.setTimeout(() => {
      if (!lovePsWindow.hidden && !lovePsWindowDrag && !lovePsMobileMedia.matches) {
        lovePsWindow.classList.add('love-ps-is-redrawing');
        window.setTimeout(() => lovePsWindow.classList.remove('love-ps-is-redrawing'), 140);
      }
    }, 180);
  }

  function lovePsResetWindowToDesktopCenter() {
    window.clearTimeout(lovePsWindowResizeTimer);
    lovePsScaler.style.removeProperty('left');
    lovePsScaler.style.removeProperty('top');
    lovePsScaler.style.removeProperty('width');
    lovePsScaler.style.removeProperty('height');
    lovePsScaler.style.removeProperty('transform');
    lovePsScaler.classList.remove('love-ps-is-maximized', 'love-ps-is-resized');
    lovePsWindowPositioned = false;
    lovePsWindowMaximized = false;
    lovePsWindowRestoreGeometry = null;
    lovePsWindowMaximize.setAttribute('aria-pressed', 'false');
    lovePsWindowMaximize.setAttribute('aria-label', 'Maximize Patch Station window');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (lovePsMobileMedia.matches) return;
        const desktopRect = lovePsRoot.getBoundingClientRect();
        const windowRect = lovePsScaler.getBoundingClientRect();
        lovePsSetWindowPosition(
          Math.max(0, (desktopRect.width - windowRect.width) / 2),
          Math.max(0, (desktopRect.height - windowRect.height) / 2)
        );
        window.clearTimeout(lovePsWindowResizeTimer);
        lovePsUpdateScrollbar();
        if (!lovePsWindow.hidden) {
          lovePsWindow.classList.add('love-ps-is-redrawing');
          window.setTimeout(() => lovePsWindow.classList.remove('love-ps-is-redrawing'), 180);
        }
      });
    });
  }

  function lovePsShowWindowDragOutline(left, top, width, height) {
    Object.assign(lovePsWindowDragOutline.style, {
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      width: `${Math.round(width)}px`,
      height: `${Math.round(height)}px`
    });
    lovePsWindowDragOutline.hidden = false;
  }

  function lovePsStartWindowDrag(event) {
    if (lovePsMobileMedia.matches || lovePsWindowMaximized || lovePsWindow.hidden || event.button !== 0 || event.target.closest('.love-ps-window-control')) return;
    event.preventDefault();
    const desktopRect = lovePsRoot.getBoundingClientRect();
    const windowRect = lovePsScaler.getBoundingClientRect();
    const originLeft = windowRect.left - desktopRect.left;
    const originTop = windowRect.top - desktopRect.top;
    const maxLeft = Math.max(0, desktopRect.width - windowRect.width);
    const maxTop = Math.max(0, desktopRect.height - windowRect.height);
    let left = originLeft;
    let top = originTop;
    lovePsWindowDrag = { pointerId: event.pointerId };
    lovePsScaler.classList.add('love-ps-is-moving');
    lovePsWindowStrobe.hidden = true;
    lovePsShowWindowDragOutline(left, top, windowRect.width, windowRect.height);
    lovePsTitlebar.setPointerCapture?.(event.pointerId);

    const move = (moveEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      left = Math.min(maxLeft, Math.max(0, originLeft + moveEvent.clientX - event.clientX));
      top = Math.min(maxTop, Math.max(0, originTop + moveEvent.clientY - event.clientY));
      lovePsWindowDragOutline.style.left = `${Math.round(left)}px`;
      lovePsWindowDragOutline.style.top = `${Math.round(top)}px`;
    };
    const finish = (endEvent, cancelled = false) => {
      if (endEvent.pointerId !== event.pointerId) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', cancel);
      if (!cancelled) lovePsSetWindowPosition(left, top);
      lovePsWindowDragOutline.hidden = true;
      lovePsScaler.classList.remove('love-ps-is-moving');
      lovePsWindowDrag = null;
      lovePsWindow.classList.add('love-ps-is-redrawing');
      window.setTimeout(() => lovePsWindow.classList.remove('love-ps-is-redrawing'), 180);
      lovePsUpdateScrollbar();
    };
    const commit = (endEvent) => finish(endEvent);
    const cancel = (cancelEvent) => finish(cancelEvent, true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', cancel);
  }

  function lovePsStartWindowResize(event) {
    if (lovePsMobileMedia.matches || lovePsWindowMaximized || lovePsWindow.hidden || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const desktopRect = lovePsRoot.getBoundingClientRect();
    const windowRect = lovePsScaler.getBoundingClientRect();
    const left = windowRect.left - desktopRect.left;
    const top = windowRect.top - desktopRect.top;
    const maxWidth = Math.max(1, desktopRect.width - left);
    const maxHeight = Math.max(1, desktopRect.height - top);
    const minWidth = Math.min(720, maxWidth);
    const minHeight = Math.min(420, maxHeight);
    let width = windowRect.width;
    let height = windowRect.height;
    lovePsWindowDrag = { pointerId: event.pointerId, resize: true };
    lovePsScaler.classList.add('love-ps-is-moving');
    lovePsShowWindowDragOutline(left, top, width, height);
    lovePsResizeHandle.setPointerCapture?.(event.pointerId);

    const move = (moveEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      width = Math.min(maxWidth, Math.max(minWidth, windowRect.width + moveEvent.clientX - event.clientX));
      height = Math.min(maxHeight, Math.max(minHeight, windowRect.height + moveEvent.clientY - event.clientY));
      lovePsWindowDragOutline.style.width = `${Math.round(width)}px`;
      lovePsWindowDragOutline.style.height = `${Math.round(height)}px`;
    };
    const finish = (endEvent, cancelled = false) => {
      if (endEvent.pointerId !== event.pointerId) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', cancel);
      if (!cancelled) {
        lovePsScaler.classList.add('love-ps-is-resized');
        lovePsScaler.style.width = `${Math.round(width)}px`;
        lovePsScaler.style.height = `${Math.round(height)}px`;
        lovePsSetWindowPosition(left, top);
      }
      lovePsWindowDragOutline.hidden = true;
      lovePsScaler.classList.remove('love-ps-is-moving');
      lovePsWindowDrag = null;
      lovePsWindow.classList.add('love-ps-is-redrawing');
      window.setTimeout(() => lovePsWindow.classList.remove('love-ps-is-redrawing'), 180);
      lovePsUpdateScrollbar();
    };
    const commit = (endEvent) => finish(endEvent);
    const cancel = (cancelEvent) => finish(cancelEvent, true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', cancel);
  }

  function lovePsToggleWindowMaximize() {
    if (lovePsMobileMedia.matches || lovePsWindow.hidden) return;
    const desktopRect = lovePsRoot.getBoundingClientRect();
    const windowRect = lovePsScaler.getBoundingClientRect();
    if (!lovePsWindowMaximized) {
      lovePsWindowRestoreGeometry = {
        left: windowRect.left - desktopRect.left,
        top: windowRect.top - desktopRect.top,
        width: windowRect.width,
        height: windowRect.height
      };
      lovePsWindowMaximized = true;
      lovePsScaler.classList.add('love-ps-is-maximized');
      lovePsWindowMaximize.setAttribute('aria-pressed', 'true');
      lovePsWindowMaximize.setAttribute('aria-label', 'Restore Patch Station window');
    } else {
      lovePsWindowMaximized = false;
      lovePsScaler.classList.remove('love-ps-is-maximized');
      const geometry = lovePsWindowRestoreGeometry;
      if (geometry) {
        lovePsScaler.style.width = `${Math.round(geometry.width)}px`;
        lovePsScaler.style.height = `${Math.round(geometry.height)}px`;
        const maxLeft = Math.max(0, desktopRect.width - geometry.width);
        const maxTop = Math.max(0, desktopRect.height - geometry.height);
        lovePsSetWindowPosition(
          Math.min(maxLeft, Math.max(0, geometry.left)),
          Math.min(maxTop, Math.max(0, geometry.top))
        );
      }
      lovePsWindowMaximize.setAttribute('aria-pressed', 'false');
      lovePsWindowMaximize.setAttribute('aria-label', 'Maximize Patch Station window');
    }
    lovePsWindow.classList.add('love-ps-is-redrawing');
    window.setTimeout(() => lovePsWindow.classList.remove('love-ps-is-redrawing'), 180);
    requestAnimationFrame(lovePsUpdateScrollbar);
  }

  function lovePsHandleWindowExpand() {
    if (!lovePsIsDedicatedPage) {
      window.location.assign(lovePsPageUrl);
      return;
    }
    lovePsToggleWindowMaximize();
  }

  function lovePsSetDesktopIconState(open) {
    lovePsDesktopIcon.setAttribute('aria-pressed', String(open));
    lovePsDesktopIconImage.src = lovePsAsset(open ? 'love-ps-desktop-icon-selected.png' : 'love-ps-desktop-icon.png');
  }

  function lovePsCloseWindow() {
    if (lovePsMobileMedia.matches) return;
    lovePsAudio.pause();
    lovePsResetRow(lovePsActiveRow);
    lovePsActiveRow = null;
    cancelAnimationFrame(lovePsFrame);
    lovePsStarfield.stop();
    lovePsWindowDragOutline.hidden = true;
    lovePsScaler.classList.remove('love-ps-is-moving', 'love-ps-is-maximized');
    lovePsWindowMaximized = false;
    lovePsWindowRestoreGeometry = null;
    lovePsWindowMaximize.setAttribute('aria-pressed', 'false');
    lovePsWindowMaximize.setAttribute('aria-label', 'Maximize Patch Station window');
    lovePsWindow.hidden = true;
    lovePsScaler.style.pointerEvents = 'none';
    lovePsSetDesktopIconState(false);
    lovePsDesktopIcon.focus();
  }

  function lovePsOpenWindow({ animate = true } = {}) {
    lovePsScaler.style.pointerEvents = '';
    if (!lovePsWindow.hidden) {
      lovePsWindowClose.focus();
      return;
    }
    lovePsWindow.hidden = false;
    lovePsSetDesktopIconState(true);
    if (!animate || lovePsMobileMedia.matches) {
      lovePsWindowClose.focus();
      lovePsUpdateScrollbar();
      lovePsStarfield.start();
      return;
    }
    lovePsWindow.classList.add('love-ps-is-opening');
    lovePsWindowStrobe.hidden = true;
    void lovePsWindowStrobe.offsetWidth;
    lovePsWindowStrobe.hidden = false;
    window.setTimeout(() => {
      lovePsWindowStrobe.hidden = true;
      lovePsWindow.classList.remove('love-ps-is-opening');
      lovePsWindow.classList.add('love-ps-is-redrawing');
      window.setTimeout(() => lovePsWindow.classList.remove('love-ps-is-redrawing'), 220);
      lovePsWindowClose.focus();
      lovePsUpdateScrollbar();
      lovePsStarfield.start();
    }, 450);
  }

  function lovePsOpenInfoWindow() {
    if (lovePsMobileMedia.matches) return;
    lovePsInfoWindow.hidden = false;
    lovePsConnectIcon.setAttribute('aria-pressed', 'true');
    lovePsConnectIconImage.src = lovePsAsset('love-ps-connect-first-love-selected.png');
    const desktopRect = lovePsRoot.getBoundingClientRect();
    const infoRect = lovePsInfoWindow.getBoundingClientRect();
    Object.assign(lovePsInfoStrobe.style, {
      left: `${infoRect.left - desktopRect.left}px`,
      top: `${infoRect.top - desktopRect.top}px`,
      width: `${infoRect.width}px`,
      height: `${infoRect.height}px`
    });
    lovePsInfoWindow.classList.add('love-ps-is-opening');
    lovePsInfoStrobe.hidden = true;
    void lovePsInfoStrobe.offsetWidth;
    lovePsInfoStrobe.hidden = false;
    window.setTimeout(() => {
      lovePsInfoStrobe.hidden = true;
      lovePsInfoWindow.classList.remove('love-ps-is-opening');
      lovePsInfoWindow.classList.add('love-ps-is-redrawing');
      window.setTimeout(() => lovePsInfoWindow.classList.remove('love-ps-is-redrawing'), 220);
      lovePsInfoOk.focus();
    }, 450);
  }

  function lovePsCloseInfoWindow() {
    lovePsInfoStrobe.hidden = true;
    lovePsInfoWindow.classList.remove('love-ps-is-opening', 'love-ps-is-redrawing');
    lovePsInfoWindow.hidden = true;
    lovePsConnectIcon.setAttribute('aria-pressed', 'false');
    lovePsConnectIconImage.src = lovePsAsset('love-ps-connect-first-love.png');
    lovePsInfoVisual.classList.remove('love-ps-is-wave-active');
    lovePsWaveController.setAttribute('aria-pressed', 'false');
    lovePsConnectIcon.focus();
  }

  function lovePsUpdateScrollbar() {
    const max = Math.max(0, lovePsList.scrollHeight - lovePsList.clientHeight);
    const trackHeight = lovePsScrollTrack.clientHeight;
    const thumbHeight = Math.max(40, Math.round(trackHeight * (lovePsList.clientHeight / lovePsList.scrollHeight)));
    const travel = Math.max(0, trackHeight - thumbHeight);
    const top = max ? Math.round((lovePsList.scrollTop / max) * travel) : 0;
    lovePsScrollThumb.style.height = `${thumbHeight}px`;
    lovePsScrollThumb.style.transform = `translateY(${top}px)`;
    lovePsScrollUp.disabled = lovePsList.scrollTop <= 0;
    lovePsScrollDown.disabled = lovePsList.scrollTop >= max - 1;
  }

  lovePsPatches.forEach((patch) => lovePsList.append(lovePsCreateRow(patch)));
  lovePsFilterPatches();
  window.requestAnimationFrame(lovePsSeedDesktopPatch);
  lovePsWindow.addEventListener('dragstart', (event) => event.preventDefault());
  lovePsAudio.addEventListener('ended', () => {
    lovePsResetRow(lovePsActiveRow);
    lovePsActiveRow = null;
    cancelAnimationFrame(lovePsFrame);
  });
  lovePsList.addEventListener('scroll', lovePsUpdateScrollbar, { passive: true });
  lovePsScrollUp.addEventListener('click', () => lovePsList.scrollBy({ top: -106, behavior: 'smooth' }));
  lovePsScrollDown.addEventListener('click', () => lovePsList.scrollBy({ top: 106, behavior: 'smooth' }));
  lovePsScrollTrack.addEventListener('click', (event) => {
    if (event.target === lovePsScrollThumb || lovePsThumbDrag) return;
    const trackRect = lovePsScrollTrack.getBoundingClientRect();
    const thumbHeight = lovePsScrollThumb.getBoundingClientRect().height;
    const travel = Math.max(1, trackRect.height - thumbHeight);
    const targetTop = Math.max(0, Math.min(travel, event.clientY - trackRect.top - (thumbHeight / 2)));
    const max = Math.max(0, lovePsList.scrollHeight - lovePsList.clientHeight);
    lovePsList.scrollTo({ top: (targetTop / travel) * max, behavior: 'smooth' });
  });
  lovePsScrollThumb.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    const trackRect = lovePsScrollTrack.getBoundingClientRect();
    const thumbRect = lovePsScrollThumb.getBoundingClientRect();
    lovePsThumbDrag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTop: thumbRect.top - trackRect.top,
      travel: Math.max(1, trackRect.height - thumbRect.height)
    };
    lovePsScrollThumb.classList.add('love-ps-is-dragging');
    lovePsScrollThumb.setPointerCapture(event.pointerId);
  });
  function lovePsMoveThumbDrag(event) {
    if (!lovePsThumbDrag || event.pointerId !== lovePsThumbDrag.pointerId) return;
    const targetTop = Math.max(0, Math.min(lovePsThumbDrag.travel, lovePsThumbDrag.startTop + event.clientY - lovePsThumbDrag.startY));
    const max = Math.max(0, lovePsList.scrollHeight - lovePsList.clientHeight);
    lovePsList.scrollTop = (targetTop / lovePsThumbDrag.travel) * max;
  }
  function lovePsEndThumbDrag(event) {
    if (!lovePsThumbDrag || event.pointerId !== lovePsThumbDrag.pointerId) return;
    lovePsScrollThumb.classList.remove('love-ps-is-dragging');
    if (lovePsScrollThumb.hasPointerCapture(event.pointerId)) lovePsScrollThumb.releasePointerCapture(event.pointerId);
    lovePsThumbDrag = null;
  }
  window.addEventListener('pointermove', lovePsMoveThumbDrag);
  window.addEventListener('pointerup', lovePsEndThumbDrag);
  window.addEventListener('pointercancel', lovePsEndThumbDrag);
  function lovePsFilterPatches() {
    const genre = lovePsGenreFilter.dataset.value;
    const categoryOrder = lovePsCategoryOrder[genre] || [];
    const categoryRank = new Map(categoryOrder.map((slug, index) => [slug, index]));
    const rows = [...lovePsList.querySelectorAll('.love-ps-row')];
    rows.sort((firstRow, secondRow) => {
      const firstSlug = firstRow.dataset.lovePsSlug;
      const secondSlug = secondRow.dataset.lovePsSlug;
      const firstRank = categoryRank.has(firstSlug)
        ? categoryRank.get(firstSlug)
        : categoryOrder.length + lovePsPatchOrder.get(firstSlug);
      const secondRank = categoryRank.has(secondSlug)
        ? categoryRank.get(secondSlug)
        : categoryOrder.length + lovePsPatchOrder.get(secondSlug);
      return firstRank - secondRank;
    });
    rows.forEach((row) => {
      lovePsList.append(row);
      const genres = row.dataset.lovePsGenres.split(' ');
      row.hidden = !genres.includes(genre);
      if (row.hidden && row.classList.contains('love-ps-is-expanded')) lovePsSetDetailsOpen(row, false);
      if (row.hidden && row === lovePsSelectedRow) {
        row.classList.remove('love-ps-is-selected');
        lovePsSelectedRow = null;
      }
    });
    lovePsList.scrollTop = 0;
    lovePsUpdateScrollbar();
  }

  function lovePsCloseGenreMenu({ focusTrigger = false } = {}) {
    lovePsGenreMenu.hidden = true;
    lovePsGenreTrigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) lovePsGenreTrigger.focus();
  }

  function lovePsOpenGenreMenu() {
    lovePsGenreMenu.hidden = false;
    lovePsGenreTrigger.setAttribute('aria-expanded', 'true');
    lovePsGenreOptions.find((option) => option.getAttribute('aria-selected') === 'true')?.focus();
  }

  function lovePsChooseGenre(option) {
    const genre = option.dataset.lovePsGenreOption;
    lovePsGenreFilter.dataset.value = genre;
    lovePsGenreValue.textContent = option.textContent;
    lovePsGenreOptions.forEach((candidate) => candidate.setAttribute('aria-selected', String(candidate === option)));
    lovePsCloseGenreMenu({ focusTrigger: true });
    lovePsFilterPatches();
  }

  lovePsGenreTrigger.addEventListener('click', () => {
    if (lovePsGenreMenu.hidden) lovePsOpenGenreMenu();
    else lovePsCloseGenreMenu();
  });
  lovePsGenreOptions.forEach((option) => option.addEventListener('click', () => lovePsChooseGenre(option)));
  lovePsGenreMenu.addEventListener('keydown', (event) => {
    const currentIndex = lovePsGenreOptions.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      lovePsGenreOptions[(currentIndex + 1) % lovePsGenreOptions.length].focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      lovePsGenreOptions[(currentIndex - 1 + lovePsGenreOptions.length) % lovePsGenreOptions.length].focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      lovePsGenreOptions[0].focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      lovePsGenreOptions[lovePsGenreOptions.length - 1].focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      lovePsCloseGenreMenu({ focusTrigger: true });
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (!lovePsGenreFilter.contains(event.target)) lovePsCloseGenreMenu();
  });
  lovePsWindowClose.addEventListener('click', lovePsCloseWindow);
  if (!lovePsIsDedicatedPage) lovePsWindowMaximize.setAttribute('aria-label', 'Open the Patch Station page');
  lovePsWindowMaximize.addEventListener('click', lovePsHandleWindowExpand);
  lovePsResizeHandle.addEventListener('pointerdown', lovePsStartWindowResize);
  lovePsTitlebar.addEventListener('pointerdown', lovePsStartWindowDrag);
  lovePsDesktopIcon.addEventListener('click', () => lovePsOpenWindow());
  lovePsConnectIcon.addEventListener('click', lovePsOpenInfoWindow);
  lovePsInfoOk.addEventListener('click', lovePsCloseInfoWindow);
  lovePsWaveController.addEventListener('pointerdown', lovePsStartWaveModulation);
  lovePsWaveController.addEventListener('pointermove', lovePsMoveWaveModulation);
  lovePsWaveController.addEventListener('pointerup', lovePsEndWaveModulation);
  lovePsWaveController.addEventListener('pointercancel', lovePsEndWaveModulation);
  lovePsWaveController.addEventListener('pointerleave', () => {
    if (!lovePsWaveDrag) lovePsWaveController.classList.remove('love-ps-just-released');
  });
  lovePsWaveController.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    lovePsWaveController.classList.remove('love-ps-just-released');
    lovePsWaveController.classList.add('love-ps-is-modulating');
    lovePsInfoVisual.classList.add('love-ps-is-wave-active');
    lovePsWaveController.setAttribute('aria-pressed', 'true');
    if (event.key === 'ArrowLeft') lovePsSetWaveModulation(lovePsWaveDuration + 100, lovePsWaveAmplitude);
    if (event.key === 'ArrowRight') lovePsSetWaveModulation(lovePsWaveDuration - 100, lovePsWaveAmplitude);
    if (event.key === 'ArrowUp') lovePsSetWaveModulation(lovePsWaveDuration, lovePsWaveAmplitude + 2);
    if (event.key === 'ArrowDown') lovePsSetWaveModulation(lovePsWaveDuration, lovePsWaveAmplitude - 2);
  });
  lovePsWaveController.addEventListener('keyup', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    lovePsWaveController.classList.remove('love-ps-is-modulating');
    lovePsWaveController.classList.add('love-ps-just-released');
    lovePsInfoVisual.classList.remove('love-ps-is-wave-active');
    lovePsWaveController.setAttribute('aria-pressed', 'false');
  });
  lovePsNewsletterButton.addEventListener('click', () => {
    const formId = lovePsNewsletterButton.dataset.lovePsKlaviyoFormId?.trim();
    lovePsNewsletterButton.dispatchEvent(new CustomEvent('love-ps-newsletter-request', {
      bubbles: true,
      detail: { source: 'connect-first-love-window', formId }
    }));
    if (!formId) return;
    lovePsCloseInfoWindow();
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  });
  lovePsInfoWindow.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') lovePsCloseInfoWindow();
  });
  lovePsMobileMedia.addEventListener('change', (event) => {
    if (event.matches) {
      lovePsInfoWindow.hidden = true;
      lovePsInfoVisual.classList.remove('love-ps-is-wave-active');
      lovePsWaveController.setAttribute('aria-pressed', 'false');
      lovePsConnectIcon.setAttribute('aria-pressed', 'false');
      lovePsConnectIconImage.src = lovePsAsset('love-ps-connect-first-love.png');
      lovePsOpenWindow({ animate: false });
    } else {
      lovePsResetWindowToDesktopCenter();
    }
  });
  window.addEventListener('resize', lovePsResize, { passive: true });
  window.visualViewport?.addEventListener('resize', lovePsResize, { passive: true });
  lovePsResize();
  requestAnimationFrame(lovePsUpdateScrollbar);
  lovePsStarfield.start();
})();
